import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import {
  servicesData as rawServices,
  productsData as rawProducts,
  shopCategories as rawShopCategories,
  bundlesData as fallbackBundlesData,
} from '@/data/content';
import {
  fetchShopifyProducts,
  registerVariant,
  metafieldMap,
  resolveFaqsFromMetafield,
  extractFaqGids,
  type ShopifyProductNode,
} from '@/lib/shopify';
import { supabase } from '@/integrations/supabase/client';
import { rowToView, type BundleRow, type BundleView } from '@/types/bundle';
import {
  rowToQuestion,
  type RxQuestion,
  type RxQuestionRow,
} from '@/types/personalRx';

type ContentContextValue = {
  services: typeof rawServices;
  products: typeof rawProducts;
  shopCategories: typeof rawShopCategories;
  bundles: BundleView[];
  reloadBundles: () => Promise<void>;
  personalRxQuestions: RxQuestion[] | null;
  reloadPersonalRxQuestions: () => Promise<void>;
  pageStringMap: Record<string, string>;
};

const ContentContext = createContext<ContentContextValue | null>(null);

// Local fallback if DB is empty / not loaded yet — mirrors the legacy shape
const fallbackBundles: BundleView[] = fallbackBundlesData.map((b) => ({
  id: b.id,
  slug: b.id,
  name: b.name,
  sub: b.sub,
  tagline: b.tagline,
  description: b.description,
  productIds: b.productIds,
  discount: b.discount,
  image: b.image,
  category: b.category,
}));

export const ContentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [shopifyProducts, setShopifyProducts] = useState<ShopifyProductNode[]>([]);
  const [bundleRows, setBundleRows] = useState<BundleRow[] | null>(null);
  const [faqMap, setFaqMap] = useState<Map<string, { question: string; answer: string }>>(
    new Map(),
  );
  const [rxQuestions, setRxQuestions] = useState<RxQuestion[] | null>(null);

  // Fetch live Shopify products once on mount
  useEffect(() => {
    fetchShopifyProducts()
      .then((items) => setShopifyProducts(items))
      .catch((e) => console.error('Failed to fetch Shopify products', e));
  }, []);

  // Resolve FAQ metaobject GIDs via the public shopify-faqs edge function
  // (Storefront token cannot read metaobjects, so we route through the server).
  useEffect(() => {
    if (!shopifyProducts.length) return;
    const gids = new Set<string>();
    shopifyProducts.forEach((p) => {
      const mf = p.metafields?.find((m) => m?.key === 'faqs') ?? null;
      extractFaqGids(mf).forEach((g) => gids.add(g));
    });
    if (gids.size === 0) return;

    const ids = Array.from(gids);
    supabase.functions
      .invoke('shopify-faqs', { body: { ids } })
      .then(({ data, error }) => {
        if (error) {
          console.error('shopify-faqs invoke error', error);
          return;
        }
        const byId = (data?.byId ?? {}) as Record<string, { question: string; answer: string }>;
        const next = new Map<string, { question: string; answer: string }>();
        Object.entries(byId).forEach(([gid, v]) => next.set(gid, v));
        setFaqMap(next);
      })
      .catch((e) => console.error('shopify-faqs failed', e));
  }, [shopifyProducts]);

  const loadBundles = async () => {
    const { data, error } = await supabase
      .from('bundles')
      .select('*')
      .eq('is_active', true)
      .order('position', { ascending: true });
    if (error) {
      console.error('Failed to load bundles', error);
      return;
    }
    setBundleRows((data as unknown as BundleRow[]) ?? []);
  };

  useEffect(() => {
    loadBundles();
    loadPersonalRxQuestions();
  }, []);

  const loadPersonalRxQuestions = async () => {
    const { data, error } = await supabase
      .from('personal_rx_questions')
      .select('*')
      .eq('is_active', true)
      .order('position', { ascending: true });
    if (error) {
      console.error('Failed to load personal_rx_questions', error);
      return;
    }
    setRxQuestions(((data as unknown as RxQuestionRow[]) ?? []).map(rowToQuestion));
  };

  // Register variants in the registry whenever Shopify products change.
  useEffect(() => {
    const bySku = new Map<string, ShopifyProductNode>();
    shopifyProducts.forEach((p) => {
      const variant = p.variants.edges[0]?.node;
      if (variant?.sku) bySku.set(variant.sku, p);
    });
    bySku.forEach((sp, sku) => {
      const variant = sp.variants.edges[0]?.node;
      if (!variant) return;
      registerVariant(sku, {
        variantId: variant.id,
        price: parseFloat(variant.price.amount),
        currencyCode: variant.price.currencyCode,
        image: sp.images.edges[0]?.node?.url,
        availableForSale: variant.availableForSale,
      });
    });
  }, [shopifyProducts]);

  const value = useMemo<ContentContextValue>(() => {
    const parseJson = <T,>(s: string | undefined, fallback: T): T => {
      if (!s) return fallback;
      try {
        return JSON.parse(s) as T;
      } catch {
        return fallback;
      }
    };

    // `long_description` lives in Shopify as `list.single_line_text_field`
    // (a JSON array string) on existing products, but the admin editor may
    // also write plain multi-line text. Accept both shapes.
    const parseParagraphs = (s: string | undefined): string[] => {
      const raw = (s ?? '').trim();
      if (!raw) return [];
      if (raw.startsWith('[')) {
        try {
          const parsed = JSON.parse(raw);
          if (Array.isArray(parsed)) {
            return parsed
              .filter((x): x is string => typeof x === 'string')
              .map((x) => x.trim())
              .filter(Boolean);
          }
        } catch {
          /* fall through to plain-text handling */
        }
      }
      return raw
        .split(/\n\s*\n/)
        .map((x) => x.trim())
        .filter(Boolean);
    };

    // Build SKU + handle lookups for products
    const shopifyBySku = new Map<string, ShopifyProductNode>();
    const shopifyByHandle = new Map<string, ShopifyProductNode>();
    shopifyProducts.forEach((p) => {
      const variant = p.variants.edges[0]?.node;
      if (variant?.sku) shopifyBySku.set(variant.sku, p);
      if (p.handle) shopifyByHandle.set(p.handle, p);
    });

    // Merge Shopify data into local product shape (match by SKU = local id)
    const mergedProducts = rawProducts.map((p) => {
      const sp = shopifyBySku.get(p.id) ?? shopifyByHandle.get(p.id);
      if (!sp) return p;
      const variant = sp.variants.edges[0]?.node;
      const allImages = sp.images.edges.map((e) => e.node.url);
      const priceStr = variant ? `₪${Math.round(parseFloat(variant.price.amount))}` : p.price;
      const compareStr = variant?.compareAtPrice
        ? `₪${Math.round(parseFloat(variant.compareAtPrice.amount))}`
        : undefined;
      const mf = metafieldMap(sp);
      const longDesc = mf.long_description
        ? parseParagraphs(mf.long_description)
        : p.longDescription;
      const benefits = parseJson<string[]>(mf.benefits, p.benefits).filter(
        (b) => typeof b === 'string' && b.trim(),
      );
      const faqsMetafield = sp.metafields?.find((m) => m?.key === 'faqs');
      const faqs = resolveFaqsFromMetafield(faqsMetafield, p.faqs, faqMap);
      return {
        ...p,
        name: sp.title || p.name,
        sub: mf.subtitle || p.sub,
        tagline: mf.tagline || p.tagline,
        description: sp.description || p.description,
        longDescription: longDesc.length ? longDesc : p.longDescription,
        benefits: benefits.length ? benefits : p.benefits,
        ingredients: mf.ingredients || p.ingredients,
        // Shopify holds this under `usage`; the admin editor also mirrors it
        // to `how_to_use`. Accept either key.
        usage: mf.usage || mf.how_to_use || p.usage,
        size: mf.size || p.size,
        price: priceStr,
        compareAtPrice: compareStr,
        image: allImages[0] ?? p.image,
        images: allImages.length ? allImages : [p.image],
        videoUrl: mf.video_url || undefined,
        faqs: faqs.length ? faqs : p.faqs,
        // Shopify productType (e.g. "Hair"/"Face"/"Body") drives frontend category
        category: sp.productType ? sp.productType.toLowerCase() : p.category,
        tags: sp.tags ?? (p as any).tags,
      };
    }) as typeof rawProducts;

    // Merge Shopify services into local service shape (match by handle = local id)
    const mergedServices = rawServices.map((s) => {
      const sp = shopifyByHandle.get(s.id);
      if (!sp) return s;
      const variant = sp.variants.edges[0]?.node;
      const mf = metafieldMap(sp);
      const priceStr = variant ? `₪${Math.round(parseFloat(variant.price.amount))}` : s.price;
      const paragraphs = mf.long_description
        ? parseParagraphs(mf.long_description)
        : (s as any).paragraphs;
      const benefits = parseJson<string[]>(mf.benefits, (s as any).benefits ?? []).filter(
        (b) => typeof b === 'string' && b.trim(),
      );
      const faqsMetafield = sp.metafields?.find((m) => m?.key === 'faqs');
      const faqs = resolveFaqsFromMetafield(faqsMetafield, (s as any).faq ?? [], faqMap);
      const firstImage = sp.images.edges[0]?.node?.url;
      return {
        ...s,
        title: sp.title || s.title,
        subtitle: mf.subtitle || (s as any).subtitle,
        tagline: mf.tagline || (s as any).tagline,
        description: sp.description || s.description,
        paragraphs: paragraphs?.length ? paragraphs : (s as any).paragraphs,
        benefits: benefits.length ? benefits : (s as any).benefits,
        suitableFor: mf.suitable_for || (s as any).suitableFor,
        beforeTreatment: mf.before_treatment || (s as any).beforeTreatment,
        afterTreatment: mf.after_treatment || (s as any).afterTreatment,
        duration: mf.duration || (s as any).duration,
        price: priceStr,
        media: firstImage || (s as any).media,
        mediaType: firstImage ? 'image' : (s as any).mediaType,
        faq: faqs.length ? faqs : (s as any).faq,
        // Service category comes from a `service-category-<cat>` tag in Shopify.
        // Fallback to productType (if it's a real category, not "Service"), then local.
        category: (() => {
          const tagCat = (sp.tags ?? [])
            .map((t) => t.toLowerCase())
            .find((t) => t.startsWith('service-category-'));
          if (tagCat) return tagCat.slice('service-category-'.length);
          if (sp.productType && !/service|שירות|טיפול/i.test(sp.productType)) {
            return sp.productType.toLowerCase();
          }
          return (s as any).category;
        })(),
        tags: sp.tags ?? (s as any).tags,
      };
    }) as typeof rawServices;

    const bundles: BundleView[] =
      bundleRows && bundleRows.length > 0 ? bundleRows.map(rowToView) : fallbackBundles;

    return {
      services: mergedServices,
      products: mergedProducts,
      shopCategories: rawShopCategories,
      bundles,
      reloadBundles: loadBundles,
      personalRxQuestions: rxQuestions,
      reloadPersonalRxQuestions: loadPersonalRxQuestions,
      pageStringMap: {},
    };
  }, [shopifyProducts, bundleRows, faqMap, rxQuestions]);

  return <ContentContext.Provider value={value}>{children}</ContentContext.Provider>;
};

export const useContent = () => {
  const ctx = useContext(ContentContext);
  if (!ctx) throw new Error('useContent must be used within ContentProvider');
  return ctx;
};

export const useT = (_id: string, fallback: string): string => fallback;
